import React, { useCallback, useEffect, useMemo, useState } from 'react';

import dayjs from '~/timeInit';

import { Check, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import { useApplyToursPricingSuggestion } from '~/hooks/apis/lere/useApplyToursPricingSuggestion';
import { useGetLereToursPricingSuggestions } from '~/hooks/apis/lere/useGetLereToursPricingSuggestions';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import { useConfirmDialog } from '../Common/Providers/ConfirmDialogProvider';

import ToursCapacitySalesFormatter from './formatters/ToursCapacitySalesFormatter';
import ToursIdsFormatter from './formatters/ToursIdsFormatter';
import ToursPricingFormatter from './formatters/ToursPricingFormatter';
import ToursScheduleFormatter from './formatters/ToursScheduleFormatter';
import { TOURS_PRICING_SAMPLE_DATA } from './toursPricingSampleData';
import {
  LerePaginatedResponse,
  LereTabComponentProps,
  LereToursPricingApplyRequest,
  LereToursPricingSuggestion,
  LereToursPricingSuggestionsParams,
  SuggestedActionType,
  ToursPricingApplyStatus,
} from './types';

const EMPTY_RESPONSE: LerePaginatedResponse<LereToursPricingSuggestion> = {
  result: [],
  total: 0,
};

const SUGGESTED_ACTION_TYPE_OPTIONS: Array<{ value: SuggestedActionType; label: string }> = [
  { value: 'surcharge', label: 'Surcharge' },
  { value: 'discount', label: 'Discount' },
];

type AppliedFilters = {
  offer_bk?: string;
  offer_salesforce_bk?: string;
  suggested_action_type?: SuggestedActionType;
};

function formatDate(value: string | null): string {
  if (!value) {
    return '—';
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : value;
}

function buildApplyRequest(
  row: LereToursPricingSuggestion,
  status: ToursPricingApplyStatus,
): LereToursPricingApplyRequest {
  return {
    suggestion_id: row.suggestion_id,
    status,
    offer_bk: row.offer_bk,
    offer_salesforce_bk: row.offer_salesforce_bk,
    offer_name: row.offer_name,
    departure_date: row.departure_date,
    departure_name: row.departure_name,
    suggested_action_type: row.suggested_action_type,
    distance_category: row.distance_category,
    original_tour_price: row.original_tour_price,
    suggested_price_change_percent: row.suggested_price_change_percent,
    after_price: row.after_price,
    total_pax_sold: row.total_pax_sold,
    max_pax: row.max_pax,
    leadtime: row.leadtime,
  };
}

function formatCurrency(value: number): string {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatSignedPercent(value: number): string {
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${(value * 100).toFixed(1)}%`;
}

export default function RMToursPricingSuggestions({ onLereError }: LereTabComponentProps) {
  const { paginationModel, setPaginationModel, setPage } = useDataGridPagination({ page: 0, pageSize: 20 });
  const showConfirmDialog = useConfirmDialog();
  const {
    mutate: applyPricingSuggestion,
    isPending: isApplying,
    variables: applyingVariables,
  } = useApplyToursPricingSuggestion();

  const [offerBkInput, setOfferBkInput] = useState('');
  const [salesforceIdInput, setSalesforceIdInput] = useState('');
  const [actionTypeInput, setActionTypeInput] = useState<SuggestedActionType | ''>('');
  const [useLiveData, setUseLiveData] = useState(true);

  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});

  const queryParams = useMemo<LereToursPricingSuggestionsParams>(
    () => ({
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      ...appliedFilters,
    }),
    [paginationModel.page, paginationModel.pageSize, appliedFilters],
  );

  const toursPricingSuggestionsQuery = useGetLereToursPricingSuggestions(queryParams, { enabled: useLiveData });

  const samplePaginatedData = useMemo<LerePaginatedResponse<LereToursPricingSuggestion>>(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;
    return {
      result: TOURS_PRICING_SAMPLE_DATA.slice(start, end),
      total: TOURS_PRICING_SAMPLE_DATA.length,
    };
  }, [paginationModel.page, paginationModel.pageSize]);

  const toursPricingSuggestionsData = useLiveData
    ? toursPricingSuggestionsQuery.data ?? EMPTY_RESPONSE
    : samplePaginatedData;

  useEffect(() => {
    if (toursPricingSuggestionsQuery.error) {
      onLereError(toursPricingSuggestionsQuery.error);
    }
  }, [toursPricingSuggestionsQuery.error, onLereError]);

  const handleSearch = () => {
    setAppliedFilters({
      offer_bk: offerBkInput || undefined,
      offer_salesforce_bk: salesforceIdInput || undefined,
      suggested_action_type: actionTypeInput || undefined,
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setOfferBkInput('');
    setSalesforceIdInput('');
    setActionTypeInput('');
    setAppliedFilters({});
    setPage(0);
  };

  const handleActionTypeChange = (event: SelectChangeEvent) => {
    setActionTypeInput(event.target.value as SuggestedActionType | '');
  };

  const handleAction = useCallback(
    async (row: LereToursPricingSuggestion, status: ToursPricingApplyStatus) => {
      if (status === 'accepted') {
        const actionLabel = row.suggested_action_type === 'surcharge' ? 'surcharge' : 'discount';
        const confirmed = await showConfirmDialog({
          title: `Apply ${actionLabel}?`,
          description:
            `This will apply a ${formatSignedPercent(row.suggested_price_change_percent)} ${actionLabel} ` +
            `to "${row.offer_name}" (departure ${formatDate(row.departure_date)}), ` +
            `changing the price from ${formatCurrency(row.original_tour_price)} to ${formatCurrency(row.after_price)}.`,
        });
        if (!confirmed) {
          return;
        }
      }

      applyPricingSuggestion(buildApplyRequest(row, status));
    },
    [showConfirmDialog, applyPricingSuggestion],
  );

  const pendingSuggestionId = isApplying ? applyingVariables?.suggestion_id : null;

  const columns = useMemo<Array<GridColDef<LereToursPricingSuggestion>>>(() => {
    const compactCol: Partial<GridColDef<LereToursPricingSuggestion>> = {
      minWidth: 120,
      flex: 1,
    };

    return [
      {
        ...compactCol,
        field: 'offer_name',
        headerName: 'Offer Name',
        minWidth: 220,
        flex: 2,
        renderCell: ({ row }) => {
          const name = row.offer_name || '—';
          if (row.offer_url) {
            return (
              <Link href={row.offer_url} target="_blank" rel="noopener noreferrer" underline="hover">
                {name}
              </Link>
            );
          }
          return name;
        },
      },
      {
        field: 'ids',
        headerName: 'IDs',
        display: 'flex',
        flex: 2,
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: ToursIdsFormatter,
      },
      {
        ...compactCol,
        field: 'suggested_action_type',
        headerName: 'Action Type',
        renderCell: ({ row }) => row.suggested_action_type.charAt(0).toUpperCase() + row.suggested_action_type.slice(1),
      },
      {
        ...compactCol,
        field: 'departure_date',
        headerName: 'Departure Date',
        minWidth: 135,
        renderCell: ({ row }) => formatDate(row.departure_date),
      },
      {
        field: 'schedule',
        headerName: 'Schedule',
        display: 'flex',
        flex: 2,
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: ToursScheduleFormatter,
      },
      {
        field: 'pricing',
        headerName: 'Pricing',
        display: 'flex',
        flex: 2,
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: ToursPricingFormatter,
      },
      {
        field: 'capacity_sales',
        headerName: 'Capacity & Sales',
        display: 'flex',
        flex: 2,
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: ToursCapacitySalesFormatter,
      },
      {
        ...compactCol,
        field: 'offer_country_name',
        headerName: 'Country',
        renderCell: ({ row }) => row.offer_country_name || '—',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        headerClassName: 'sticky-actions-header',
        cellClassName: 'sticky-actions-cell',
        renderCell: ({ row }) => {
          if (pendingSuggestionId === row.suggestion_id) {
            return <CircularProgress size={24} />;
          }

          return (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ py: 1 }}>
              <Tooltip title="Accept & apply pricing change">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleAction(row, 'accepted')}
                  disabled={isApplying}
                >
                  <Check fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reject recommendation">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleAction(row, 'rejected')}
                  disabled={isApplying}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ];
  }, [pendingSuggestionId, isApplying, handleAction]);

  return (
    <Box>
      <Typography variant="h2" fontSize="2em" fontWeight="medium">
        RM Tours Pricing Suggestions
      </Typography>
      <Typography variant="subtitle1" fontWeight="medium" sx={{ mt: 3 }}>
        Filters
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1, mb: 3 }}>
        <TextField
          label="Offer ID"
          size="small"
          disabled={!useLiveData}
          value={offerBkInput}
          onChange={(e) => setOfferBkInput(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="Salesforce ID"
          size="small"
          disabled={!useLiveData}
          value={salesforceIdInput}
          onChange={(e) => setSalesforceIdInput(e.target.value)}
          sx={{ minWidth: 200 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }} disabled={!useLiveData}>
          <InputLabel>Action Type</InputLabel>
          <Select value={actionTypeInput} label="Action Type" onChange={handleActionTypeChange}>
            <MenuItem value="">All</MenuItem>
            {SUGGESTED_ACTION_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="text"
          disabled={!useLiveData || toursPricingSuggestionsQuery.isFetching}
          onClick={handleClearFilters}
        >
          Clear filters
        </Button>
        <Button
          variant="contained"
          disabled={!useLiveData || toursPricingSuggestionsQuery.isFetching}
          onClick={handleSearch}
        >
          Search
        </Button>
      </Stack>

      <DataGrid
        autoHeight
        columns={columns}
        rows={toursPricingSuggestionsData.result}
        rowCount={toursPricingSuggestionsData.total}
        loading={useLiveData && toursPricingSuggestionsQuery.isFetching}
        pagination
        paginationMode="server"
        pageSizeOptions={[10, 20, 50]}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowHeight={() => 'auto'}
        getRowId={(row) => row.suggestion_id}
        slots={{
          pagination: GridPagination,
        }}
        disableRowSelectionOnClick
        disableColumnMenu
        sx={{
          width: '100%',
          '& .sticky-actions-header, & .sticky-actions-cell': {
            position: 'sticky',
            right: 0,
            overflow: 'visible',
            backgroundColor: 'background.paper',
          },
          '& .sticky-actions-header': {
            zIndex: 6,
          },
          '& .sticky-actions-cell': {
            zIndex: 5,
          },
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'transparent',
          },
          // Hide the resize separator on the actions header and the column immediately before it
          '& .sticky-actions-header .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-columnHeader:has(+ .sticky-actions-header) .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
        }}
      />

      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Switch
              checked={useLiveData}
              onChange={(e) => {
                setUseLiveData(e.target.checked);
                setPage(0);
              }}
            />
          }
          label={
            <Typography variant="body2" color={useLiveData ? 'text.primary' : 'text.secondary'}>
              Use Live Data {!useLiveData && '(currently showing sample data)'}
            </Typography>
          }
        />
      </Box>
    </Box>
  );
}
